import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSeo, SITE_URL } from '../lib/seo.js';

/**
 * <Seo /> — renders all SEO and social meta tags for the current route.
 *
 * Drop this near the top of any page component. It auto-detects the current
 * route via useLocation() and pulls metadata from src/lib/seo.js.
 *
 * If a page wants to override a specific field, pass it as a prop:
 *   <Seo title="Custom title" />
 */
export function Seo(props) {
  const location = useLocation();
  const data = getSeo(location.pathname);

  // Allow per-render overrides while falling back to the configured metadata.
  const title = props.title || data.title;
  const description = props.description || data.description;
  const keywords = props.keywords || data.keywords;
  const ogImage = props.ogImage || data.ogImage;

  // Absolute URL for og:image (Open Graph requires absolute URLs)
  const absoluteOgImage = ogImage.startsWith('http')
    ? ogImage
    : `${SITE_URL}${ogImage}`;
  const canonical = `${SITE_URL}${location.pathname}`;

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary meta */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph (used by Facebook, LinkedIn, Discord, Slack, iMessage) */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="NerdStack" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
    </Helmet>
  );
}
